import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import api from '@/services/api.service';
import {
  Modal,
  Card,
  Tabs,
  Form,
  Select,
  DatePicker,
  Input,
  Button,
  Table,
  InputNumber,
  Space,
  Alert,
  Tag,
  Spin,
  App,
  Typography,
  Tooltip,
} from 'antd';

const { Text } = Typography;
import type { Item } from '../../services/inventory.service';
import { ItemSelect } from '../inventory/ItemSelect';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useApplyUserDefaultDepartment } from '@/utils/userDefaultDepartment';
import {
  CriticalItemTag,
  getCriticalItemRowClassName,
  isItemCritical,
  ItemCodeWithCritical,
} from '../inventory/criticalItemUi';
import { PlusOutlined, DeleteOutlined, WarningOutlined, CheckCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useCreateMaterialRequest,
  useCreateMaterialRequestFromBOM,
  useCreateMaterialRequestFromBoqEstimation,
  useBoqEstimationPreviewLines,
  useCheckInventoryAvailability,
  useMaterialRequest,
  useMaterialRequestBomOptions,
  useUpdateMaterialRequest,
} from '../../hooks/useMaterialRequest';
import { useCheckWorkOrderBlocked } from '../../hooks/useQCWorkflow';
import { useItems, useWarehouses } from '../../hooks/useInventory';
import {
  CreateMaterialRequestDTO,
  InventoryAvailability,
  type BoqEstimationPreviewLine,
} from '../../services/materialRequest.service';
import bomService from '../../services/bom.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { AedDirhamSign } from '@/components/currency';
import workOrderService from '@/services/workOrder.service';
import { isFabricationExecutionScope } from '@/utils/executionScope';
import { useBoqBomLineOptions } from '@/hooks/useWorkOrder';
import type { BoqBomLineOption } from '@/services/workOrder.service';
import { MeasurementDimensionInputs } from '@/components/inventory/MeasurementDimensionInputs';
import {
  MEASUREMENT_TYPE_LABELS,
  computeMeasuredQuantity,
  measuredQuantityWarning,
  normalizeMeasurementType,
  parseDimensionToMm,
  type MeasurementDims,
  type MeasurementType,
} from '@/utils/inventory/measurementQuantity';

function formatBoqLineQtyPart(opt: BoqBomLineOption): string {
  const qty = Number(opt.planned_quantity);
  return Number.isFinite(qty)
    ? ` · ${qty.toLocaleString(undefined, { maximumFractionDigits: 4 })} ${opt.uom || ''}`.trimEnd()
    : '';
}

function formatBoqLineOptionLabel(opt: BoqBomLineOption): string {
  return `Line ${opt.work_order_line_number}: ${opt.bom_name}${formatBoqLineQtyPart(opt)}`;
}

function formatBoqLineDetailLabel(opt: BoqBomLineOption): string {
  return `${opt.bom_name}${formatBoqLineQtyPart(opt)}`;
}

type BoqLineSelectOption = {
  value: string;
  label: string;
  boq_line_item_id?: string | null;
};

/**
 * Compact control ("Line N") + full wrapped description under it so long BOQ
 * text stays readable without blowing out the table layout.
 */
function BoqLineCellSelect({
  value,
  options,
  lineOptions,
  placeholder,
  onChange,
}: {
  value?: string;
  options: BoqLineSelectOption[];
  lineOptions: BoqBomLineOption[];
  placeholder: string;
  onChange: (workOrderLineItemId: string) => void;
}) {
  const selected = lineOptions.find((o) => o.work_order_line_item_id === value);
  return (
    <div style={{ maxWidth: 320, width: '100%' }}>
      <Select
        allowClear
        showSearch
        placeholder={placeholder}
        style={{ width: '100%' }}
        value={value}
        options={options}
        optionFilterProp="label"
        popupMatchSelectWidth={false}
        styles={{ popup: { root: { maxWidth: 560, minWidth: 360 } } }}
        labelRender={() =>
          selected ? `Line ${selected.work_order_line_number}` : undefined
        }
        optionRender={(ori) => (
          <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35 }}>
            {ori.label}
          </div>
        )}
        onChange={(v) => onChange(v ? String(v) : '')}
      />
      {selected ? (
        <Text
          type="secondary"
          style={{
            display: 'block',
            fontSize: 12,
            lineHeight: 1.35,
            marginTop: 4,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
        >
          {formatBoqLineDetailLabel(selected)}
        </Text>
      ) : null}
    </div>
  );
}

function encodeHeatDimBucket(heat: string, dimension: string): string {
  return JSON.stringify({ h: heat.trim(), d: dimension.trim() });
}

function decodeHeatDimBucket(s: string): { heat: string; dimension: string } {
  try {
    const o = JSON.parse(s) as { h?: string; d?: string };
    return { heat: String(o.h ?? '').trim(), dimension: String(o.d ?? '').trim() };
  } catch {
    return { heat: '', dimension: '' };
  }
}

/** Stock / consumable-like types that can carry warehouse buckets */
function isStockishItemType(itemType: string | undefined): boolean {
  if (!itemType) return false;
  return !['Service', 'Non-Stock', 'Subcontract'].includes(itemType);
}

/** Show heat/dimension picker for any critical stockish item (not only when heat_number_mandatory is set). */
function needsMrHeatDimPicker(it: Item | undefined): boolean {
  if (!it) return false;
  return isStockishItemType(it.item_type) && Boolean(it.is_critical);
}

/** Require a selected bucket before check / create when master data mandates heat traceability. */
function needsMrHeatDimRequired(it: Item | undefined): boolean {
  return needsMrHeatDimPicker(it) && Boolean(it?.heat_number_mandatory);
}

async function fetchStockByHeat(itemId: string, warehouseId: string) {
  const res = (await api.get(`/inventory/items/${itemId}/stock-by-heat`, {
    params: { warehouse_id: warehouseId },
  })) as { data?: unknown };
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (d && typeof d === 'object' && Array.isArray((d as { data?: unknown }).data)) {
    return (d as { data: unknown[] }).data;
  }
  return [];
}

interface MaterialRequestDialogProps {
  visible?: boolean;
  /** Existing draft MR — edit mode (manual lines + header fields). */
  materialRequestId?: string;
  /** Manufacturing WO — required unless maintenanceCaseId is set */
  workOrderId?: string;
  /** Workshop / Asset Maintenance Case — creates MR with AssetMaintenanceCase reference */
  maintenanceCaseId?: string;
  /** Project-scoped manual material request (no WO / BOM) */
  projectId?: string;
  onClose: () => void;
  onSuccess?: () => void;
  /** Prepended to saved notes (e.g. WO / SO / BOQ context). */
  contextNotesPrefix?: string;
  /** Render as full page card instead of modal popup. */
  mode?: 'modal' | 'page';
  title?: string;
  onBack?: () => void;
}

interface ManualRequestItem {
  key: string;
  item_id: string;
  item_code?: string;
  item_name?: string;
  /** Pieces when the line has a measurement type; otherwise quantity in UOM. */
  requested_quantity: number;
  /** Legacy free factor — only used when measurement_type is null (read-only in the grid). */
  area_dimension: number;
  measurement_type?: MeasurementType | null;
  length_mm?: number | null;
  width_mm?: number | null;
  height_mm?: number | null;
  uom: string;
  estimated_unit_cost?: number;
  heat_number?: string;
  dimension?: string;
  is_critical?: boolean;
  work_order_line_item_id?: string | null;
  boq_line_item_id?: string | null;
}

function lineMeasurement(
  type: MeasurementType | null | undefined,
  dims: MeasurementDims | null | undefined,
  uom: string | null | undefined
) {
  return computeMeasuredQuantity({ type, pieces: 1, dims, uom });
}

function hasAnyDimension(dims: MeasurementDims | null | undefined): boolean {
  return [dims?.length_mm, dims?.width_mm, dims?.height_mm].some(
    (v) => v != null && v !== '' && Number(v) > 0
  );
}

function dimsPayload(dims: MeasurementDims | null | undefined) {
  const n = (v: unknown) => (v != null && v !== '' && Number(v) > 0 ? Number(v) : null);
  return { length_mm: n(dims?.length_mm), width_mm: n(dims?.width_mm), height_mm: n(dims?.height_mm) };
}

export const MaterialRequestDialog: React.FC<MaterialRequestDialogProps> = ({
  visible = true,
  materialRequestId,
  workOrderId = '',
  maintenanceCaseId,
  projectId,
  onClose,
  onSuccess,
  contextNotesPrefix,
  mode = 'modal',
  title = 'Create Material Request',
  onBack,
}) => {
  const isEdit = Boolean(materialRequestId?.trim());
  const isMaintenanceCase = Boolean(maintenanceCaseId?.trim());
  const isProject = Boolean(projectId?.trim());
  const [form] = Form.useForm();
  const { message } = App.useApp();
  useApplyUserDefaultDepartment(form, visible && !isEdit);
  const { formatCurrency } = useCurrency();
  const [activeTab, setActiveTab] = useState<'bom' | 'boq' | 'manual'>(
    isEdit || isMaintenanceCase || isProject ? 'manual' : 'bom'
  );
  const editPrefillDone = useRef(false);
  const [manualItems, setManualItems] = useState<ManualRequestItem[]>([]);
  const [manualAvailabilityByKey, setManualAvailabilityByKey] = useState<Record<string, InventoryAvailability>>(
    {}
  );
  const [bomTraceByLineId, setBomTraceByLineId] = useState<Record<string, string>>({});
  const [boqTraceByLineKey, setBoqTraceByLineKey] = useState<Record<string, string>>({});
  const [boqItemIdByLineKey, setBoqItemIdByLineKey] = useState<Record<string, string>>({});
  const [boqAvailabilityByLineKey, setBoqAvailabilityByLineKey] = useState<
    Record<string, InventoryAvailability>
  >({});
  const [boqSelectedKeys, setBoqSelectedKeys] = useState<string[]>([]);
  const [bomSelectedLineKeys, setBomSelectedLineKeys] = useState<string[]>([]);
  /** BOQ preview line_key → piece dimensions (mm); when set the BOQ qty is treated as pieces */
  const [boqDimsByLineKey, setBoqDimsByLineKey] = useState<Record<string, MeasurementDims>>({});
  /** BOM component line id → piece dimensions (mm) */
  const [bomDimsByLineId, setBomDimsByLineId] = useState<Record<string, MeasurementDims>>({});
  const [bomAvailabilityByLineId, setBomAvailabilityByLineId] = useState<
    Record<string, InventoryAvailability>
  >({});
  /** BOM component line id → WO line item id */
  const [bomWoLineByBomLineId, setBomWoLineByBomLineId] = useState<Record<string, string>>({});
  /** BOQ preview line_key → WO line item id (editable override) */
  const [boqWoLineByLineKey, setBoqWoLineByLineKey] = useState<Record<string, string>>({});
  const bomFormDefaultsDone = useRef(false);

  const mergeRequestNotes = (userNotes: string | undefined) => {
    const u = userNotes?.trim();
    const p = contextNotesPrefix?.trim();
    if (p && u) return `${p}\n\n${u}`;
    if (p) return p;
    return u || undefined;
  };

  const normalizeAreaDimension = (value: unknown) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return 1;
    return Math.round(n * 10000) / 10000;
  };

  const toEffectiveQty = (baseQty: number, areaDimension: number) =>
    Math.round(baseQty * areaDimension * 10000) / 10000;

  const { data: items = [], isLoading: loadingItems } = useItems();
  const { data: warehouses = [] } = useWarehouses();
  const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  const itemMeasurementType = (itemId: string | null | undefined): MeasurementType | null =>
    itemId ? normalizeMeasurementType(itemsById.get(itemId)?.measurement_type) : null;

  /** Measured lines send dimensions (server derives the factor); legacy lines keep their saved factor. */
  const manualLinePayload = (item: ManualRequestItem) =>
    item.measurement_type
      ? { area_dimension: 1, ...dimsPayload(item) }
      : { area_dimension: normalizeAreaDimension(item.area_dimension) };

  /** Manual line factor: type-driven dimensions, else the saved legacy factor. */
  const manualLineFactor = (item: ManualRequestItem): number =>
    item.measurement_type
      ? lineMeasurement(item.measurement_type, item, item.uom).factor
      : normalizeAreaDimension(item.area_dimension);

  /** BOQ / BOM tab factor: 1 unless the user entered dimensions for a measured item. */
  const tabLineFactor = (
    itemId: string | null | undefined,
    uom: string | null | undefined,
    dims: MeasurementDims | undefined
  ): number => {
    const type = itemMeasurementType(itemId);
    if (!type || !hasAnyDimension(dims)) return 1;
    return lineMeasurement(type, dims, uom).factor;
  };

  const renderTabDimensions = (
    itemId: string | null | undefined,
    uom: string | null | undefined,
    dims: MeasurementDims | undefined,
    onChange: (next: MeasurementDims) => void
  ) => {
    const type = itemMeasurementType(itemId);
    if (!type) return <Text type="secondary">—</Text>;
    const measured = hasAnyDimension(dims) ? lineMeasurement(type, dims, uom) : null;
    const warn = measured ? measuredQuantityWarning(type, uom, measured.reason) : null;
    return (
      <>
        <MeasurementDimensionInputs
          type={type}
          value={dims}
          onChange={(k, v) => onChange({ ...(dims ?? {}), [k]: v })}
        />
        <div style={{ fontSize: 11 }}>
          {warn ? (
            <Text type="warning">{warn}</Text>
          ) : (
            <Text type="secondary">Optional — qty becomes pieces</Text>
          )}
        </div>
      </>
    );
  };
  /** Ensure selected MR lines appear as labeled options (avoids raw UUID in the closed Select). */
  const itemsForManualSelect = useMemo(() => {
    const byId = new Map(items.map((i) => [i.id, i]));
    for (const row of manualItems) {
      if (!row.item_id) continue;
      const existing = byId.get(row.item_id);
      const code = String(row.item_code ?? '').trim();
      const name = String(row.item_name ?? '').trim();
      if (!existing) {
        byId.set(row.item_id, {
          id: row.item_id,
          item_code: code || row.item_id,
          item_name: name || 'Item',
          base_uom: row.uom,
          is_critical: row.is_critical,
          item_type: 'Stock',
          is_active: true,
        } as Item);
      } else if ((code || name) && (!existing.item_code?.trim() || !existing.item_name?.trim())) {
        byId.set(row.item_id, {
          ...existing,
          item_code: existing.item_code?.trim() || code || existing.id,
          item_name: existing.item_name?.trim() || name || 'Item',
        });
      }
    }
    return Array.from(byId.values());
  }, [items, manualItems]);
  const createFromBOMMutation = useCreateMaterialRequestFromBOM();
  const createFromBoqEstMutation = useCreateMaterialRequestFromBoqEstimation();
  const createManualMutation = useCreateMaterialRequest();
  const updateMutation = useUpdateMaterialRequest();
  const { data: existingMr, isLoading: loadingExistingMr } = useMaterialRequest(materialRequestId || '', {
    enabled: isEdit && visible,
  });
  const checkAvailabilityMutation = useCheckInventoryAvailability();
  const { data: iirBlockData, isLoading: iirBlockLoading } = useCheckWorkOrderBlocked(workOrderId, {
    enabled: visible && !isEdit && !!String(workOrderId).trim(),
  });
  const iirBlocksMr = !isEdit && Boolean(iirBlockData?.isBlocked);
  const [boqIssueForUnit, setBoqIssueForUnit] = useState<number | 'full'>('full');

  const { data: woForMr } = useQuery({
    queryKey: ['work-orders', workOrderId, 'mr-scope'],
    queryFn: () => workOrderService.getById(workOrderId),
    enabled: visible && !!workOrderId?.trim(),
  });
  const isFabricationWo = isFabricationExecutionScope(woForMr?.execution_scope);
  const fabricationPlannedQty = Math.max(
    1,
    Math.floor(Number(woForMr?.planned_quantity) || 1)
  );

  const boqPreview = useBoqEstimationPreviewLines(workOrderId, {
    enabled: visible && !!workOrderId && activeTab === 'boq',
    production_unit_number:
      isFabricationWo && boqIssueForUnit !== 'full' ? boqIssueForUnit : undefined,
  });

  const bomOptionsQuery = useMaterialRequestBomOptions(workOrderId, {
    enabled: visible && !!workOrderId && activeTab === 'bom',
  });

  const showBoqLinePicker = Boolean(workOrderId?.trim()) && !isMaintenanceCase && !isProject;
  const { data: boqLineOptions = [] } = useBoqBomLineOptions(workOrderId, {
    enabled: visible && showBoqLinePicker,
  });
  const boqLineSelectOptions = useMemo(
    () =>
      boqLineOptions.map((opt) => ({
        value: opt.work_order_line_item_id,
        label: formatBoqLineOptionLabel(opt),
        boq_line_item_id: opt.boq_line_item_id,
      })),
    [boqLineOptions]
  );

  const selectedBomId = Form.useWatch('bom_id', form) as string | undefined;
  const watchedWarehouseId = Form.useWatch('source_warehouse_id', form) as string | undefined;

  const bomDetailQuery = useQuery({
    queryKey: ['manufacturing-boms', 'detail', selectedBomId],
    queryFn: () => bomService.getById(selectedBomId!),
    enabled: visible && activeTab === 'bom' && !!selectedBomId,
  });
  const bomDetail = bomDetailQuery.data;

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setManualItems([]);
      setManualAvailabilityByKey({});
      setBomTraceByLineId({});
      setBoqTraceByLineKey({});
      setBoqItemIdByLineKey({});
      setBoqAvailabilityByLineKey({});
      setBoqSelectedKeys([]);
      setBomSelectedLineKeys([]);
      setBoqDimsByLineKey({});
      setBomDimsByLineId({});
      setBomAvailabilityByLineId({});
      setBomWoLineByBomLineId({});
      setBoqWoLineByLineKey({});
      setBoqIssueForUnit('full');
      bomFormDefaultsDone.current = false;
      editPrefillDone.current = false;
      setActiveTab(isEdit || isMaintenanceCase || isProject ? 'manual' : 'bom');
    }
  }, [visible, form, isEdit, isMaintenanceCase, isProject]);

  useEffect(() => {
    if (!visible || !isEdit || !existingMr || editPrefillDone.current) return;
    form.setFieldsValue({
      source_warehouse_id: existingMr.source_warehouse_id || undefined,
      required_date: existingMr.required_date ? dayjs(existingMr.required_date) : undefined,
      priority: existingMr.priority || 'Normal',
      department_id: existingMr.department_id || undefined,
      notes: existingMr.notes || undefined,
    });
    setActiveTab('manual');
    setManualItems(
      (existingMr.items || []).map((item, index) => {
        const area = normalizeAreaDimension(item.area_dimension);
        const effective = Number(item.requested_quantity || 0);
        const base =
          item.base_quantity != null && Number(item.base_quantity) > 0
            ? Number(item.base_quantity)
            : area > 0
              ? Math.round((effective / area) * 10000) / 10000
              : effective;
        const master = itemsById.get(item.item_id);
        const snapType = normalizeMeasurementType(item.measurement_type);
        const currentType = normalizeMeasurementType(
          item.item_measurement_type ?? master?.measurement_type
        );
        const snapDims = dimsPayload(item);
        const measurementType =
          snapType && hasAnyDimension(snapDims) ? snapType : area !== 1 ? null : snapType ?? currentType;
        return {
          key: item.id || `edit-${index}`,
          item_id: item.item_id,
          item_code: item.item_code || master?.item_code,
          item_name: item.item_name || master?.item_name,
          requested_quantity: base,
          area_dimension: area,
          measurement_type: measurementType,
          ...(measurementType ? snapDims : { length_mm: null, width_mm: null, height_mm: null }),
          uom: item.uom || master?.base_uom || '',
          estimated_unit_cost: item.estimated_unit_cost,
          heat_number: item.heat_number,
          dimension: item.dimension,
          is_critical: item.is_critical ?? master?.is_critical,
          work_order_line_item_id: item.work_order_line_item_id ?? null,
          boq_line_item_id: item.boq_line_item_id ?? null,
        };
      })
    );
    editPrefillDone.current = true;
  }, [visible, isEdit, existingMr, form, itemsById]);

  // When the item master finishes loading after draft prefill, backfill missing codes/names.
  useEffect(() => {
    if (!isEdit || !items.length) return;
    setManualItems((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (!row.item_id) return row;
        const master = itemsById.get(row.item_id);
        if (!master) return row;
        const item_code = row.item_code?.trim() ? row.item_code : master.item_code;
        const item_name = row.item_name?.trim() ? row.item_name : master.item_name;
        const uom = row.uom?.trim() ? row.uom : master.base_uom || row.uom;
        const is_critical = row.is_critical ?? master.is_critical;
        if (
          item_code === row.item_code &&
          item_name === row.item_name &&
          uom === row.uom &&
          is_critical === row.is_critical
        ) {
          return row;
        }
        changed = true;
        return { ...row, item_code, item_name, uom, is_critical };
      });
      return changed ? next : prev;
    });
  }, [isEdit, items, itemsById]);

  useEffect(() => {
    if (visible && (isMaintenanceCase || isEdit)) {
      setActiveTab('manual');
    }
  }, [visible, isMaintenanceCase, isEdit]);

  useEffect(() => {
    if (!visible || activeTab !== 'bom' || bomFormDefaultsDone.current) return;
    const boms = bomOptionsQuery.data?.boms;
    if (!boms?.length) return;
    const preferred = boms.find((b) => b.is_linked_on_work_order) ?? boms[0];
    form.setFieldValue('bom_id', preferred.id);
    bomFormDefaultsDone.current = true;
  }, [visible, activeTab, bomOptionsQuery.data, form]);

  useEffect(() => {
    setBomAvailabilityByLineId({});
    setBomTraceByLineId({});
  }, [selectedBomId]);

  useEffect(() => {
    const items = bomDetailQuery.data?.items;
    if (!items?.length) {
      setBomSelectedLineKeys([]);
      return;
    }
    setBomSelectedLineKeys(items.map((row) => row.id));
  }, [bomDetailQuery.data]);

  useEffect(() => {
    if (!visible || activeTab !== 'boq') return;
    if (boqPreview.isSuccess && Array.isArray(boqPreview.data)) {
      setBoqSelectedKeys(boqPreview.data.map((r) => r.line_key));
    }
  }, [visible, activeTab, boqPreview.isSuccess, boqPreview.data]);

  useEffect(() => {
    setBoqAvailabilityByLineKey({});
    setBoqTraceByLineKey({});
    setBoqItemIdByLineKey({});
  }, [boqPreview.data]);

  useEffect(() => {
    const rows = boqPreview.data;
    if (!rows?.length) {
      setBoqWoLineByLineKey({});
      return;
    }
    setBoqWoLineByLineKey((prev) => {
      const next: Record<string, string> = {};
      for (const row of rows) {
        const fromPrev = prev[row.line_key];
        const fromRow = String(row.work_order_line_item_id ?? '').trim();
        if (fromPrev) next[row.line_key] = fromPrev;
        else if (fromRow) next[row.line_key] = fromRow;
      }
      return next;
    });
  }, [boqPreview.data]);

  const resolveBoqItemId = (row: { line_key: string; item_id?: string | null; needs_inventory_link?: boolean }) => {
    const linked = String(row.item_id ?? '').trim();
    if (linked) return linked;
    return String(boqItemIdByLineKey[row.line_key] ?? '').trim();
  };

  const bomScaleFactor = useMemo(() => {
    const planned = bomOptionsQuery.data?.work_order?.planned_quantity;
    const bomQty = bomDetail?.quantity;
    if (planned == null || bomQty == null || Number(bomQty) <= 0) return 1;
    return Number(planned) / Number(bomQty);
  }, [bomOptionsQuery.data?.work_order?.planned_quantity, bomDetail?.quantity]);

  const manualStockQueries = useQueries({
    queries: manualItems.map((it) => ({
      queryKey: ['mr-dialog-manual-stock-heat', it.key, it.item_id, watchedWarehouseId],
      queryFn: () => fetchStockByHeat(it.item_id, String(watchedWarehouseId)),
      enabled: Boolean(
        visible && watchedWarehouseId && it.item_id && needsMrHeatDimPicker(itemsById.get(it.item_id))
      ),
    })),
  });

  const bomStockQueries = useQueries({
    queries: (bomDetail?.items ?? []).map((row) => ({
      queryKey: ['mr-dialog-bom-stock-heat', row.id, row.item_id, watchedWarehouseId, selectedBomId],
      queryFn: () => fetchStockByHeat(row.item_id, String(watchedWarehouseId)),
      enabled: Boolean(
        visible &&
        activeTab === 'bom' &&
        watchedWarehouseId &&
        selectedBomId &&
        row.item_id &&
        bomSelectedLineKeys.includes(row.id) &&
          needsMrHeatDimPicker(itemsById.get(row.item_id))
      ),
    })),
  });

  const boqStockQueries = useQueries({
    queries: (boqPreview.data ?? []).map((row) => {
      const resolvedItemId = resolveBoqItemId(row);
      return {
        queryKey: ['mr-dialog-boq-stock-heat', row.line_key, resolvedItemId, watchedWarehouseId],
        queryFn: () => fetchStockByHeat(resolvedItemId, String(watchedWarehouseId)),
        enabled: Boolean(
          visible &&
          activeTab === 'boq' &&
          watchedWarehouseId &&
          resolvedItemId &&
          boqSelectedKeys.includes(row.line_key) &&
          needsMrHeatDimPicker(itemsById.get(resolvedItemId))
        ),
      };
    }),
  });

  const stockRowsToSelectOptions = (raw: unknown[]) =>
    raw
      .filter((r) => {
        const row = r as { heat_number?: string; dimension?: string; available_quantity?: number };
        const h = String(row.heat_number ?? '').trim();
        const d = String(row.dimension ?? '').trim();
        return Boolean(h && d);
      })
      .map((r) => {
        const row = r as { heat_number?: string; dimension?: string; available_quantity?: number };
        const hn = String(row.heat_number).trim();
        const dim = String(row.dimension ?? '').trim();
        return {
          value: encodeHeatDimBucket(hn, dim),
          label: `${hn} / ${dim} — avail ${Number(row.available_quantity).toFixed(2)}`,
        };
      });

  const manualHeatMetaByLineKey = useMemo(() => {
    const m = new Map<string, { loading: boolean; options: { label: string; value: string }[] }>();
    manualItems.forEach((it, idx) => {
      if (!needsMrHeatDimPicker(itemsById.get(it.item_id))) return;
      const q = manualStockQueries[idx];
      const raw = Array.isArray(q.data) ? q.data : [];
      m.set(it.key, { loading: Boolean(q.isLoading), options: stockRowsToSelectOptions(raw) });
    });
    return m;
  }, [manualItems, manualStockQueries, itemsById]);

  const bomHeatMetaByLineId = useMemo(() => {
    const rows = bomDetail?.items ?? [];
    const m = new Map<string, { loading: boolean; options: { label: string; value: string }[] }>();
    rows.forEach((row, idx) => {
      if (!needsMrHeatDimPicker(itemsById.get(row.item_id))) return;
      const q = bomStockQueries[idx];
      const raw = Array.isArray(q.data) ? q.data : [];
      m.set(row.id, { loading: Boolean(q.isLoading), options: stockRowsToSelectOptions(raw) });
    });
    return m;
  }, [bomDetail?.items, bomStockQueries, itemsById]);

  const boqHeatMetaByLineKey = useMemo(() => {
    const rows = boqPreview.data ?? [];
    const m = new Map<string, { loading: boolean; options: { label: string; value: string }[] }>();
    rows.forEach((row, idx) => {
      const resolvedItemId = resolveBoqItemId(row);
      if (!resolvedItemId || !needsMrHeatDimPicker(itemsById.get(resolvedItemId))) return;
      const q = boqStockQueries[idx];
      const raw = Array.isArray(q.data) ? q.data : [];
      m.set(row.line_key, { loading: Boolean(q.isLoading), options: stockRowsToSelectOptions(raw) });
    });
    return m;
  }, [boqPreview.data, boqStockQueries, itemsById, boqItemIdByLineKey]);

  const handleCheckBomAvailability = async () => {
    try {
      const lines = bomDetail?.items ?? [];
      if (lines.length === 0) {
        message.warning('Select a BOM with components first.');
        return;
      }
      const selected = new Set(bomSelectedLineKeys);
      const rows = lines.filter((r) => selected.has(r.id));
      if (rows.length === 0) {
        message.warning('Select at least one component line in the table.');
        return;
      }
      const warehouseId = form.getFieldValue('source_warehouse_id');
      if (!warehouseId) {
        message.warning(
          'Select a source warehouse so availability matches stock in that warehouse (same as opening stock / issue).'
        );
        return;
      }
      for (const row of rows) {
        const inv = itemsById.get(row.item_id);
        if (needsMrHeatDimRequired(inv)) {
          const enc = bomTraceByLineId[row.id];
          const { heat, dimension } = enc ? decodeHeatDimBucket(enc) : { heat: '', dimension: '' };
          if (!heat || !dimension) {
            message.warning(
              `Select heat and dimension for critical component "${row.item_name ?? row.item_id}" before checking stock.`
            );
            return;
          }
        }
      }

      const itemsToCheck = rows.map((r) => {
        const inv = itemsById.get(r.item_id);
        const enc = bomTraceByLineId[r.id];
        const { heat, dimension } = enc ? decodeHeatDimBucket(enc) : { heat: '', dimension: '' };
        const passBucket = needsMrHeatDimPicker(inv) && Boolean(heat && dimension);
        return {
          item_id: r.item_id,
          requested_quantity: toEffectiveQty(
            Math.round(Number(r.quantity) * bomScaleFactor * 10000) / 10000,
            tabLineFactor(r.item_id, r.uom, bomDimsByLineId[r.id])
          ),
          warehouse_id: warehouseId,
          heat_number: passBucket ? heat : undefined,
          dimension: passBucket ? dimension : undefined,
        };
      });
      const result = await checkAvailabilityMutation.mutateAsync(itemsToCheck);
      const next: Record<string, InventoryAvailability> = {};
      rows.forEach((row, i) => {
        if (result[i]) next[row.id] = result[i];
      });
      setBomAvailabilityByLineId(next);
    } catch (error) {
      console.error('BOM availability check failed:', error);
      message.error((error as any)?.response?.data?.message || 'Failed to check availability');
    }
  };

  const handleCreateFromBOM = async () => {
    try {
      if (iirBlockLoading || iirBlocksMr) {
        message.warning(
          iirBlocksMr
            ? 'Job Inspection must be approved before requesting materials.'
            : 'Still checking Job Inspection status. Please wait.'
        );
        return;
      }
      const values = await form.validateFields([
        'source_warehouse_id',
        'bom_id',
        'required_date',
        'priority',
        'notes',
        'department_id',
      ]);

      if (bomSelectedLineKeys.length === 0) {
        message.error('Select at least one BOM component line.');
        return;
      }

      const lines = bomDetail?.items ?? [];
      const selected = new Set(bomSelectedLineKeys);
      const bom_line_traceability: Record<string, { heat_number: string; dimension: string }> = {};
      const bom_line_dimensions: Record<string, ReturnType<typeof dimsPayload>> = {};
      for (const row of lines) {
        if (!selected.has(row.id)) continue;
        if (itemMeasurementType(row.item_id) && hasAnyDimension(bomDimsByLineId[row.id])) {
          bom_line_dimensions[row.id] = dimsPayload(bomDimsByLineId[row.id]);
        }
        const inv = itemsById.get(row.item_id);
        if (!needsMrHeatDimPicker(inv)) continue;
        const enc = bomTraceByLineId[row.id];
        const { heat, dimension } = enc ? decodeHeatDimBucket(enc) : { heat: '', dimension: '' };
        if (needsMrHeatDimRequired(inv) && (!heat || !dimension)) {
          message.error(
            `Select heat and dimension for critical component "${row.item_name ?? row.item_id}" before creating the request.`
          );
          return;
        }
        if (heat && dimension) {
          bom_line_traceability[row.id] = { heat_number: heat, dimension };
        }
      }

      await createFromBOMMutation.mutateAsync({
        workOrderId,
        warehouseId: values.source_warehouse_id,
        bomId: values.bom_id,
        bomLineIds: bomSelectedLineKeys,
        requiredDate: values.required_date?.format('YYYY-MM-DD'),
        priority: values.priority,
        notes: mergeRequestNotes(values.notes),
        departmentId: values.department_id,
        bomLineTraceability:
          Object.keys(bom_line_traceability).length > 0 ? bom_line_traceability : undefined,
        bomLineDimensions:
          Object.keys(bom_line_dimensions).length > 0 ? bom_line_dimensions : undefined,
        workOrderLineByBomLineId:
          Object.keys(bomWoLineByBomLineId).length > 0 ? bomWoLineByBomLineId : undefined,
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Validation error:', error);
    }
  };

  const handleCreateFromBoqEstimation = async () => {
    try {
      if (iirBlockLoading || iirBlocksMr) {
        message.warning(
          iirBlocksMr
            ? 'Job Inspection must be approved before requesting materials.'
            : 'Still checking Job Inspection status. Please wait.'
        );
        return;
      }
      const values = await form.validateFields(['source_warehouse_id', 'required_date', 'priority', 'notes', 'department_id']);

      if (boqPreview.isPending) {
        message.info('Still loading BOQ lines…');
        return;
      }
      if (boqPreview.isError) {
        message.error(
          (boqPreview.error as any)?.response?.data?.message ||
            'Could not load BOQ lines for this work order.'
        );
        return;
      }
      if (!boqPreview.data || boqPreview.data.length === 0) {
        message.warning(
          'No BOQ materials found for this work order. Add materials on the BOQ (linked or description-only) or use another tab.'
        );
        return;
      }
      if (boqSelectedKeys.length === 0) {
        message.error('Select at least one line in the table below.');
        return;
      }

      const boqRows = boqPreview.data ?? [];
      const selectedBoq = new Set(boqSelectedKeys);
      const boq_line_traceability: Record<string, { heat_number: string; dimension: string }> = {};
      const boq_line_dimensions: Record<string, ReturnType<typeof dimsPayload>> = {};
      const item_id_by_line_key: Record<string, string> = {};
      for (const row of boqRows) {
        if (!selectedBoq.has(row.line_key)) continue;
        const resolvedItemId = resolveBoqItemId(row);
        if (itemMeasurementType(resolvedItemId) && hasAnyDimension(boqDimsByLineKey[row.line_key])) {
          boq_line_dimensions[row.line_key] = dimsPayload(boqDimsByLineKey[row.line_key]);
        }
        if (row.needs_inventory_link || !String(row.item_id ?? '').trim()) {
          if (!resolvedItemId) {
            message.error(
              `Link an inventory item for "${row.item_name ?? row.item_code ?? 'BOQ material'}" before creating the request.`
            );
            return;
          }
          item_id_by_line_key[row.line_key] = resolvedItemId;
        }
        const inv = itemsById.get(resolvedItemId);
        if (!needsMrHeatDimPicker(inv)) continue;
        const enc = boqTraceByLineKey[row.line_key];
        const { heat, dimension } = enc ? decodeHeatDimBucket(enc) : { heat: '', dimension: '' };
        if (needsMrHeatDimRequired(inv) && (!heat || !dimension)) {
          message.error(
            `Select heat and dimension for critical line "${row.item_name ?? row.item_code}" before creating the request.`
          );
          return;
        }
        if (heat && dimension) {
          boq_line_traceability[row.line_key] = { heat_number: heat, dimension };
        }
      }

      await createFromBoqEstMutation.mutateAsync({
        workOrderId,
        warehouseId: values.source_warehouse_id,
        requiredDate: values.required_date?.format('YYYY-MM-DD'),
        priority: values.priority,
        notes: mergeRequestNotes(values.notes),
        departmentId: values.department_id,
        lineKeys: boqSelectedKeys,
        itemIdByLineKey:
          Object.keys(item_id_by_line_key).length > 0 ? item_id_by_line_key : undefined,
        boqLineTraceability:
          Object.keys(boq_line_traceability).length > 0 ? boq_line_traceability : undefined,
        boqLineDimensions:
          Object.keys(boq_line_dimensions).length > 0 ? boq_line_dimensions : undefined,
        production_unit_number:
          isFabricationWo && boqIssueForUnit !== 'full' ? boqIssueForUnit : undefined,
        workOrderLineByLineKey:
          Object.keys(boqWoLineByLineKey).length > 0 ? boqWoLineByLineKey : undefined,
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Validation or API error:', error);
    }
  };

  const handleCreateManual = async () => {
    try {
      if (iirBlockLoading || iirBlocksMr) {
        message.warning(
          iirBlocksMr
            ? 'Job Inspection must be approved before requesting materials.'
            : 'Still checking Job Inspection status. Please wait.'
        );
        return;
      }
      // Validate only this tab’s fields. `validateFields()` with no args also validates BOM’s
      // `bom_id` (required) because all tab panes stay mounted, which blocks Manual creation.
      const values = await form.validateFields([
        'source_warehouse_id',
        'required_date',
        'priority',
        'notes',
        'department_id',
      ]);

      if (manualItems.length === 0) {
        message.error('Please add at least one item');
        return;
      }

      for (const item of manualItems) {
        const inv = itemsById.get(item.item_id);
        if (needsMrHeatDimRequired(inv)) {
          const h = String(item.heat_number ?? '').trim();
          const d = String(item.dimension ?? '').trim();
          if (!h || !d) {
            message.error(
              `Select heat and dimension for critical item "${item.item_name ?? item.item_code ?? item.item_id}".`
            );
            return;
          }
        }
      }

      const dto: CreateMaterialRequestDTO = {
        ...(isMaintenanceCase
          ? {
              context_kind: 'MaintenanceCase',
              work_order_id: null,
              reference_type: 'AssetMaintenanceCase',
              reference_id: maintenanceCaseId!,
            }
          : isProject
            ? {
                context_kind: 'Project',
                project_id: projectId!,
                work_order_id: null,
              }
            : { context_kind: 'WorkOrder', work_order_id: workOrderId }),
        request_type: 'Manual',
        required_date: values.required_date?.format('YYYY-MM-DD'),
        priority: values.priority || 'Normal',
        source_warehouse_id: values.source_warehouse_id,
        department_id: values.department_id,
        notes: mergeRequestNotes(values.notes),
        items: manualItems.map(item => ({
          item_id: item.item_id,
          item_code: item.item_code,
          item_name: item.item_name,
          requested_quantity: item.requested_quantity,
          ...manualLinePayload(item),
          uom: item.uom,
          estimated_unit_cost: item.estimated_unit_cost,
          bom_reference: false,
          heat_number: String(item.heat_number ?? '').trim() || undefined,
          dimension: String(item.dimension ?? '').trim() || undefined,
          work_order_line_item_id: item.work_order_line_item_id || null,
          boq_line_item_id: item.boq_line_item_id || null,
        })),
      };

      await createManualMutation.mutateAsync(dto);
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Validation error:', error);
    }
  };

  const handleUpdateDraft = async () => {
    if (!materialRequestId) return;
    try {
      const values = await form.validateFields([
        'source_warehouse_id',
        'required_date',
        'priority',
        'notes',
        'department_id',
      ]);

      if (manualItems.length === 0) {
        message.error('Please add at least one item');
        return;
      }

      for (const item of manualItems) {
        if (!item.item_id) {
          message.error('Please select an item for every line');
          return;
        }
        const inv = itemsById.get(item.item_id);
        if (needsMrHeatDimRequired(inv)) {
          const h = String(item.heat_number ?? '').trim();
          const d = String(item.dimension ?? '').trim();
          if (!h || !d) {
            message.error(
              `Select heat and dimension for critical item "${item.item_name ?? item.item_code ?? item.item_id}".`
            );
            return;
          }
        }
      }

      await updateMutation.mutateAsync({
        id: materialRequestId,
        data: {
          required_date: values.required_date?.format('YYYY-MM-DD'),
          priority: values.priority || 'Normal',
          source_warehouse_id: values.source_warehouse_id,
          department_id: values.department_id,
          notes: values.notes?.trim() || undefined,
          items: manualItems.map((item) => ({
            item_id: item.item_id,
            item_code: item.item_code,
            item_name: item.item_name,
            requested_quantity: item.requested_quantity,
            ...manualLinePayload(item),
            uom: item.uom,
            estimated_unit_cost: item.estimated_unit_cost,
            heat_number: String(item.heat_number ?? '').trim() || undefined,
            dimension: String(item.dimension ?? '').trim() || undefined,
            work_order_line_item_id: item.work_order_line_item_id || null,
            boq_line_item_id: item.boq_line_item_id || null,
          })),
        },
      });
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Validation or update error:', error);
    }
  };

  const handleAddItem = () => {
    setManualItems([
      ...manualItems,
      {
        key: Date.now().toString(),
        item_id: '',
        requested_quantity: 1,
        area_dimension: 1,
        measurement_type: null,
        uom: '',
      },
    ]);
  };

  const handleRemoveItem = (key: string) => {
    setManualItems(manualItems.filter(item => item.key !== key));
  };

  const handleManualHeatDimChange = (key: string, encoded: string | undefined) => {
    if (!encoded) {
      setManualItems((prev) =>
        prev.map((item) =>
          item.key === key ? { ...item, heat_number: '', dimension: '' } : item
        )
      );
      return;
    }
    const { heat, dimension } = decodeHeatDimBucket(encoded);
    setManualItems((prev) =>
      prev.map((item) => (item.key === key ? { ...item, heat_number: heat, dimension } : item))
    );
  };

  const handleItemChange = (key: string, field: string, value: any) => {
    setManualItems(prev => prev.map(item => {
      if (item.key === key) {
        if (field === 'item_id') {
          const selectedItem = items.find(i => i.id === value);
          return {
            ...item,
            item_id: value,
            item_code: selectedItem?.item_code,
            item_name: selectedItem?.item_name,
            uom: selectedItem?.base_uom || item.uom,
            estimated_unit_cost: selectedItem?.standard_cost || selectedItem?.average_cost || item.estimated_unit_cost,
            heat_number: '',
            dimension: '',
            is_critical: selectedItem ? Boolean(selectedItem.is_critical) : false,
            area_dimension: 1,
            measurement_type: normalizeMeasurementType(selectedItem?.measurement_type),
            length_mm: parseDimensionToMm(selectedItem?.profile_length),
            width_mm: selectedItem?.default_width_mm != null ? Number(selectedItem.default_width_mm) : null,
            height_mm: selectedItem?.default_height_mm != null ? Number(selectedItem.default_height_mm) : null,
          };
        }
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  const handleCheckAvailability = async () => {
    if (manualItems.length === 0) {
      message.warning('Please add items first');
      return;
    }

    const warehouseId = form.getFieldValue('source_warehouse_id');
    if (!warehouseId) {
      message.warning('Select a source warehouse so availability matches stock in that warehouse (same as opening stock / issue).');
      return;
    }
    const filtered = manualItems.filter(item => item.item_id && item.requested_quantity > 0);
    for (const item of filtered) {
      const inv = itemsById.get(item.item_id);
      if (needsMrHeatDimRequired(inv)) {
        const h = String(item.heat_number ?? '').trim();
        const d = String(item.dimension ?? '').trim();
        if (!h || !d) {
          message.warning(
            `Select heat and dimension for critical item "${item.item_name ?? item.item_code}" before checking stock.`
          );
          return;
        }
      }
    }

    const itemsToCheck = filtered.map(item => {
      const inv = itemsById.get(item.item_id);
      const h = String(item.heat_number ?? '').trim();
      const d = String(item.dimension ?? '').trim();
      const passBucket = needsMrHeatDimPicker(inv) && Boolean(h && d);
      return {
        item_id: item.item_id,
        requested_quantity: toEffectiveQty(Number(item.requested_quantity || 0), manualLineFactor(item)),
        warehouse_id: warehouseId,
        heat_number: passBucket ? h : undefined,
        dimension: passBucket ? d : undefined,
      };
    });

    if (itemsToCheck.length === 0) {
      message.warning('Please select items and quantities');
      return;
    }

    const result = await checkAvailabilityMutation.mutateAsync(itemsToCheck);
    const next: Record<string, InventoryAvailability> = {};
    filtered.forEach((row, i) => {
      if (result[i]) next[row.key] = result[i];
    });
    setManualAvailabilityByKey(next);
  };

  const handleCheckBoqAvailability = async () => {
    try {
      const rows = boqPreview.data ?? [];
      if (rows.length === 0) {
        message.warning('No BOQ lines loaded yet.');
        return;
      }
      const selected = new Set(boqSelectedKeys);
      const linesToCheck = rows.filter((r) => selected.has(r.line_key));
      if (linesToCheck.length === 0) {
        message.warning('Select at least one line in the table to check stock.');
        return;
      }

      const warehouseId = form.getFieldValue('source_warehouse_id');
      if (!warehouseId) {
        message.warning(
          'Select a source warehouse so availability matches stock in that warehouse (same as opening stock / issue).'
        );
        return;
      }

      for (const line of linesToCheck) {
        if (!resolveBoqItemId(line)) {
          message.warning(
            `Link an inventory item for "${line.item_name ?? line.item_code ?? 'BOQ material'}" before checking stock.`
          );
          return;
        }
      }

      const linesForApi = linesToCheck
        .map((r) => ({ ...r, resolved_item_id: resolveBoqItemId(r) }))
        .filter((r) => r.resolved_item_id && r.requested_quantity > 0);

      for (const line of linesForApi) {
        const inv = itemsById.get(line.resolved_item_id);
        if (needsMrHeatDimRequired(inv)) {
          const enc = boqTraceByLineKey[line.line_key];
          const { heat, dimension } = enc ? decodeHeatDimBucket(enc) : { heat: '', dimension: '' };
          if (!heat || !dimension) {
            message.warning(
              `Select heat and dimension for critical line "${line.item_name ?? line.item_code ?? line.resolved_item_id}" before checking stock.`
            );
            return;
          }
        }
      }

      const itemsToCheck = linesForApi.map((r) => {
        const inv = itemsById.get(r.resolved_item_id);
        const enc = boqTraceByLineKey[r.line_key];
        const { heat, dimension } = enc ? decodeHeatDimBucket(enc) : { heat: '', dimension: '' };
        const passBucket = needsMrHeatDimPicker(inv) && Boolean(heat && dimension);
        return {
          item_id: r.resolved_item_id,
          requested_quantity: toEffectiveQty(
            Number(r.requested_quantity || 0),
            tabLineFactor(r.resolved_item_id, r.uom, boqDimsByLineKey[r.line_key])
          ),
          warehouse_id: warehouseId,
          heat_number: passBucket ? heat : undefined,
          dimension: passBucket ? dimension : undefined,
        };
      });

      if (itemsToCheck.length === 0) {
        message.warning('No valid lines to check.');
        return;
      }

      const result = await checkAvailabilityMutation.mutateAsync(itemsToCheck);
      const next: Record<string, InventoryAvailability> = {};
      linesForApi.forEach((line, i) => {
        if (result[i]) next[line.line_key] = result[i];
      });
      setBoqAvailabilityByLineKey(next);
    } catch (error) {
      console.error('BOQ availability check failed:', error);
      message.error((error as any)?.response?.data?.message || 'Failed to check availability');
    }
  };

  const getManualAvailabilityStatus = (lineKey: string) => {
    const avail = manualAvailabilityByKey[lineKey];
    if (!avail) return null;

    if (avail.requires_heat_dimension) {
      return (
        <Tag color="default">Select heat + dimension to check this line</Tag>
      );
    }

    return avail.is_sufficient ? (
      <Tag icon={<CheckCircleOutlined />} color="success">
        Free: {avail.free_quantity ?? avail.available_quantity}
        {avail.on_hand_quantity != null ? ` (on hand ${avail.on_hand_quantity})` : ''}
      </Tag>
    ) : (
      <Tag icon={<WarningOutlined />} color="warning">
        Free: {avail.free_quantity ?? avail.available_quantity}
        {avail.on_hand_quantity != null ? ` / on hand ${avail.on_hand_quantity}` : ''}
      </Tag>
    );
  };

  const manualItemsColumns = [
    {
      title: 'Item',
      dataIndex: 'item_id',
      key: 'item_id',
      width: 320,
      render: (_: any, record: ManualRequestItem) => (
        <Space size={6} wrap style={{ width: '100%' }}>
          <ItemSelect
            items={itemsForManualSelect}
            autoLoad={false}
            placeholder={
              loadingItems ? 'Loading items...' : items.length === 0 ? 'No items found' : 'Select item'
            }
            value={record.item_id || undefined}
            onChange={(value) => handleItemChange(record.key, 'item_id', value)}
            loading={loadingItems}
            style={{ width: 260 }}
            popupMatchSelectWidth={false}
            styles={{ popup: { root: { maxWidth: 520 } } }}
            notFoundContent={loadingItems ? <Spin size="small" /> : 'No items available'}
          />
          <CriticalItemTag critical={isItemCritical(record)} />
        </Space>
      ),
    },
    ...(showBoqLinePicker && boqLineSelectOptions.length > 0
      ? [
          {
            title: 'BOQ line',
            key: 'boq_line',
            width: 320,
            render: (_: unknown, record: ManualRequestItem) => (
              <BoqLineCellSelect
                placeholder="Optional BOQ line"
                value={record.work_order_line_item_id || undefined}
                options={boqLineSelectOptions}
                lineOptions={boqLineOptions}
                onChange={(woLineId) => {
                  const opt = boqLineOptions.find((o) => o.work_order_line_item_id === woLineId);
                  setManualItems((prev) =>
                    prev.map((item) =>
                      item.key === record.key
                        ? {
                            ...item,
                            work_order_line_item_id: woLineId || null,
                            boq_line_item_id: opt?.boq_line_item_id ?? null,
                          }
                        : item
                    )
                  );
                }}
              />
            ),
          },
        ]
      : []),
    {
      title: 'Qty / Pieces',
      dataIndex: 'requested_quantity',
      key: 'requested_quantity',
      width: 120,
      render: (_: any, record: ManualRequestItem) => (
        <>
          <InputNumber
            min={0.01}
            value={record.requested_quantity}
            onChange={(value) => handleItemChange(record.key, 'requested_quantity', value || 0)}
            style={{ width: '100%' }}
          />
          {record.measurement_type ? (
            <Text type="secondary" style={{ fontSize: 11 }}>
              pieces
            </Text>
          ) : null}
        </>
      ),
    },
    {
      title: 'Dimensions (mm)',
      key: 'dimensions',
      width: 170,
      render: (_: any, record: ManualRequestItem) => {
        if (record.measurement_type) {
          return (
            <>
              <MeasurementDimensionInputs
                type={record.measurement_type}
                value={record}
                onChange={(k, v) => handleItemChange(record.key, k, v)}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                {MEASUREMENT_TYPE_LABELS[record.measurement_type]}
              </Text>
            </>
          );
        }
        const legacy = normalizeAreaDimension(record.area_dimension);
        if (legacy !== 1) {
          return (
            <Tooltip title="Saved before item measurement types; kept as-is.">
              <Text type="secondary">Factor (legacy) × {legacy}</Text>
            </Tooltip>
          );
        }
        return <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Effective Qty',
      key: 'effective_requested_quantity',
      width: 150,
      render: (_: any, record: ManualRequestItem) => {
        const total = toEffectiveQty(Number(record.requested_quantity || 0), manualLineFactor(record));
        const measured = record.measurement_type
          ? lineMeasurement(record.measurement_type, record, record.uom)
          : null;
        const warn = measured
          ? measuredQuantityWarning(record.measurement_type, record.uom, measured.reason)
          : null;
        return (
          <>
            {total.toLocaleString(undefined, { maximumFractionDigits: 4 })}
            {measured?.derived ? (
              <div style={{ fontSize: 11, color: '#666' }}>
                {record.requested_quantity} pcs × {measured.factor}
              </div>
            ) : null}
            {warn ? (
              <div style={{ fontSize: 11 }}>
                <Text type="warning">{warn}</Text>
              </div>
            ) : null}
          </>
        );
      },
    },
    {
      title: 'UOM',
      dataIndex: 'uom',
      key: 'uom',
      width: 80,
      render: (_: any, record: ManualRequestItem) => record.uom || '-',
    },
    {
      title: 'Heat / dimension',
      key: 'heat_dim',
      width: 280,
      render: (_: any, record: ManualRequestItem) => {
        const inv = itemsById.get(record.item_id);
        if (!needsMrHeatDimPicker(inv)) return '—';
        if (!watchedWarehouseId) {
          return <Text type="secondary">Select warehouse</Text>;
        }
        const meta = manualHeatMetaByLineKey.get(record.key);
        if (meta?.loading) return <Spin size="small" />;
        const h = String(record.heat_number ?? '').trim();
        const d = String(record.dimension ?? '').trim();
        const selectValue = h && d ? encodeHeatDimBucket(h, d) : undefined;
        return (
          <Select
            showSearch
            allowClear
            placeholder="Heat + dimension"
            style={{ width: '100%' }}
            value={selectValue}
            onChange={(v) => {
              handleManualHeatDimChange(record.key, v);
              setManualAvailabilityByKey((prev) => {
                const next = { ...prev };
                delete next[record.key];
                return next;
              });
            }}
            options={meta?.options ?? []}
            optionFilterProp="label"
            popupMatchSelectWidth={false}
            filterOption={(input, option) =>
              String(option?.label ?? '')
                .toLowerCase()
                .includes(input.trim().toLowerCase())
            }
          />
        );
      },
    },
    {
      title: 'Est. Cost',
      dataIndex: 'estimated_unit_cost',
      key: 'estimated_unit_cost',
      width: 120,
      render: (_: any, record: ManualRequestItem) => (
        <InputNumber
          min={0}
          precision={2}
          value={record.estimated_unit_cost}
          onChange={(value) => handleItemChange(record.key, 'estimated_unit_cost', value || 0)}
          style={{ width: '100%' }}
          prefix={<AedDirhamSign />}
        />
      ),
    },
    {
      title: 'Availability',
      key: 'availability',
      width: 150,
      render: (_: any, record: ManualRequestItem) => getManualAvailabilityStatus(record.key),
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_: any, record: ManualRequestItem) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveItem(record.key)}
        />
      ),
    },
  ];

  const boqPreviewColumns = useMemo(
    () => [
      {
        title: 'Inventory link',
        key: 'inventory_link',
        width: 220,
        render: (
          _: unknown,
          record: {
            line_key: string;
            item_id?: string | null;
            needs_inventory_link?: boolean;
          }
        ) => {
          const linked = String(record.item_id ?? '').trim();
          if (linked && !record.needs_inventory_link) {
            return <Tag color="green">Linked</Tag>;
          }
          return (
            <ItemSelect
              items={items}
              autoLoad={false}
              loading={loadingItems}
              placeholder="Select inventory item"
              value={boqItemIdByLineKey[record.line_key] || undefined}
              onChange={(v) => {
                const id = v ? String(v) : '';
                setBoqItemIdByLineKey((prev) => {
                  const next = { ...prev };
                  if (!id) delete next[record.line_key];
                  else next[record.line_key] = id;
                  return next;
                });
                setBoqTraceByLineKey((prev) => {
                  const next = { ...prev };
                  delete next[record.line_key];
                  return next;
                });
                setBoqAvailabilityByLineKey((prev) => {
                  const next = { ...prev };
                  delete next[record.line_key];
                  return next;
                });
              }}
            />
          );
        },
      },
      ...(showBoqLinePicker && boqLineSelectOptions.length > 0
        ? [
            {
              title: 'BOQ line',
              key: 'boq_line',
              width: 320,
              render: (
                _: unknown,
                record: {
                  line_key: string;
                  work_order_line_item_id?: string | null;
                  boq_line_label?: string | null;
                }
              ) => (
                <BoqLineCellSelect
                  placeholder="BOQ line"
                  value={boqWoLineByLineKey[record.line_key] || record.work_order_line_item_id || undefined}
                  options={boqLineSelectOptions}
                  lineOptions={boqLineOptions}
                  onChange={(woLineId) => {
                    setBoqWoLineByLineKey((prev) => {
                      const next = { ...prev };
                      if (!woLineId) delete next[record.line_key];
                      else next[record.line_key] = woLineId;
                      return next;
                    });
                  }}
                />
              ),
            },
          ]
        : [
            {
              title: 'BOQ line',
              key: 'boq_line_label',
              width: 320,
              render: (_: unknown, record: { boq_line_label?: string | null }) => (
                <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {record.boq_line_label || '—'}
                </span>
              ),
            },
          ]),
      {
        title: 'Item code',
        dataIndex: 'item_code',
        key: 'item_code',
        width: 140,
        render: (
          v: string | undefined,
          record: { item_code?: string; item_id?: string | null; line_key: string; is_critical?: boolean }
        ) => {
          const resolvedId = resolveBoqItemId(record);
          const inv = resolvedId ? itemsById.get(resolvedId) : undefined;
          return (
            <ItemCodeWithCritical
              code={v || inv?.item_code}
              critical={isItemCritical(inv) || Boolean(record.is_critical)}
            />
          );
        },
      },
      {
        title: 'Item name',
        dataIndex: 'item_name',
        key: 'item_name',
        ellipsis: true,
      },
      {
        title: 'Qty',
        dataIndex: 'requested_quantity',
        key: 'requested_quantity',
        width: 100,
        render: (q: number) => (typeof q === 'number' ? q.toLocaleString() : q),
      },
      {
        title: 'Dimensions (mm)',
        key: 'dimensions',
        width: 170,
        render: (_: unknown, record: BoqEstimationPreviewLine) =>
          renderTabDimensions(
            resolveBoqItemId(record),
            record.uom,
            boqDimsByLineKey[record.line_key],
            (next) => setBoqDimsByLineKey((prev) => ({ ...prev, [record.line_key]: next }))
          ),
      },
      {
        title: 'Effective Qty',
        key: 'effective_qty',
        width: 130,
        render: (_: unknown, record: BoqEstimationPreviewLine) =>
          toEffectiveQty(
            Number(record.requested_quantity || 0),
            tabLineFactor(resolveBoqItemId(record), record.uom, boqDimsByLineKey[record.line_key])
          ).toLocaleString(undefined, { maximumFractionDigits: 4 }),
      },
      { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 72 },
      {
        title: 'Est. unit',
        dataIndex: 'estimated_unit_cost',
        key: 'estimated_unit_cost',
        width: 110,
        render: (c: number | undefined) =>
          c != null && Number.isFinite(Number(c)) ? formatCurrency(Number(c)) : '—',
      },
      {
        title: 'Heat / dimension',
        key: 'heat_dim',
        width: 280,
        render: (_: unknown, record: { line_key: string; item_id?: string | null }) => {
          const resolvedId = resolveBoqItemId(record);
          const inv = resolvedId ? itemsById.get(resolvedId) : undefined;
          if (!needsMrHeatDimPicker(inv)) return '—';
          if (!watchedWarehouseId) {
            return <Text type="secondary">Select warehouse</Text>;
          }
          const meta = boqHeatMetaByLineKey.get(record.line_key);
          if (meta?.loading) return <Spin size="small" />;
          const enc = boqTraceByLineKey[record.line_key];
          const { heat, dimension } = enc ? decodeHeatDimBucket(enc) : { heat: '', dimension: '' };
          const selectValue = heat && dimension ? encodeHeatDimBucket(heat, dimension) : undefined;
          return (
            <Select
              showSearch
              allowClear
              placeholder="Heat + dimension"
              style={{ width: '100%' }}
              value={selectValue}
              onChange={(v) => {
                setBoqTraceByLineKey((prev) => {
                  const next = { ...prev };
                  if (!v) delete next[record.line_key];
                  else next[record.line_key] = v;
                  return next;
                });
                setBoqAvailabilityByLineKey((prev) => {
                  const next = { ...prev };
                  delete next[record.line_key];
                  return next;
                });
              }}
              options={meta?.options ?? []}
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.trim().toLowerCase())
              }
            />
          );
        },
      },
      {
        title: 'Availability',
        key: 'availability',
        width: 150,
        render: (_: unknown, record: { line_key: string }) => {
          const avail = boqAvailabilityByLineKey[record.line_key];
          if (!avail) return null;
          if (avail.requires_heat_dimension) {
            return <Tag color="default">Select heat + dimension</Tag>;
          }
          return avail.is_sufficient ? (
            <Tag icon={<CheckCircleOutlined />} color="success">
              Free: {avail.free_quantity ?? avail.available_quantity}
                {avail.on_hand_quantity != null ? ` (on hand ${avail.on_hand_quantity})` : ''}
            </Tag>
          ) : (
            <Tag icon={<WarningOutlined />} color="warning">
              Free: {avail.free_quantity ?? avail.available_quantity}
                {avail.on_hand_quantity != null ? ` / on hand ${avail.on_hand_quantity}` : ''}
            </Tag>
          );
        },
      },
    ],
    [
      boqAvailabilityByLineKey,
      boqItemIdByLineKey,
      items,
      itemsById,
      loadingItems,
      watchedWarehouseId,
      boqHeatMetaByLineKey,
      boqTraceByLineKey,
      boqDimsByLineKey,
      formatCurrency,
      showBoqLinePicker,
      boqLineSelectOptions,
      boqWoLineByLineKey,
    ]
  );

  const boqPreviewErrorMessage =
    (boqPreview.error as any)?.response?.data?.message ||
    (boqPreview.error as Error | undefined)?.message;

  const bomOptionsErrorMessage =
    (bomOptionsQuery.error as any)?.response?.data?.message ||
    (bomOptionsQuery.error as Error | undefined)?.message;

  const bomColumns = useMemo(
    () => [
      {
        title: 'Component',
        dataIndex: 'item_name',
        key: 'item_name',
        ellipsis: true,
        render: (name: string, record: { item_id: string; item_code?: string }) => {
          const inv = itemsById.get(record.item_id);
          return (
            <Space size={6} wrap>
              <span>{record.item_code ? `${record.item_code} — ${name}` : name}</span>
              <CriticalItemTag critical={isItemCritical(inv)} />
            </Space>
          );
        },
      },
      ...(showBoqLinePicker && boqLineSelectOptions.length > 0
        ? [
            {
              title: 'BOQ line',
              key: 'boq_line',
              width: 320,
              render: (_: unknown, record: { id: string }) => (
                <BoqLineCellSelect
                  placeholder="Optional BOQ line"
                  value={bomWoLineByBomLineId[record.id] || undefined}
                  options={boqLineSelectOptions}
                  lineOptions={boqLineOptions}
                  onChange={(woLineId) => {
                    setBomWoLineByBomLineId((prev) => {
                      const next = { ...prev };
                      if (!woLineId) delete next[record.id];
                      else next[record.id] = woLineId;
                      return next;
                    });
                  }}
                />
              ),
            },
          ]
        : []),
      {
        title: 'BOM qty',
        dataIndex: 'quantity',
        key: 'quantity',
        width: 100,
        render: (q: number) => (typeof q === 'number' ? q.toLocaleString() : q),
      },
      {
        title: 'Qty for WO',
        key: 'scaled',
        width: 120,
        render: (_: unknown, record: { quantity: number }) => {
          const s = Number(record.quantity) * bomScaleFactor;
          return Number.isFinite(s)
            ? s.toLocaleString(undefined, { maximumFractionDigits: 4 })
            : '—';
        },
      },
      {
        title: 'Dimensions (mm)',
        key: 'dimensions',
        width: 170,
        render: (_: unknown, record: { id: string; item_id: string; uom?: string }) =>
          renderTabDimensions(record.item_id, record.uom, bomDimsByLineId[record.id], (next) =>
            setBomDimsByLineId((prev) => ({ ...prev, [record.id]: next }))
          ),
      },
      {
        title: 'Effective Qty',
        key: 'effective_qty',
        width: 130,
        render: (_: unknown, record: { id: string; item_id: string; uom?: string; quantity: number }) => {
          const baseQty = Math.round(Number(record.quantity) * bomScaleFactor * 10000) / 10000;
          return toEffectiveQty(
            baseQty,
            tabLineFactor(record.item_id, record.uom, bomDimsByLineId[record.id])
          ).toLocaleString(undefined, { maximumFractionDigits: 4 });
        },
      },
      { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 72 },
      {
        title: 'Heat / dimension',
        key: 'heat_dim',
        width: 280,
        render: (_: unknown, record: { id: string; item_id: string }) => {
          const inv = itemsById.get(record.item_id);
          if (!needsMrHeatDimPicker(inv)) return '—';
          if (!watchedWarehouseId) {
            return <Text type="secondary">Select warehouse</Text>;
          }
          const meta = bomHeatMetaByLineId.get(record.id);
          if (meta?.loading) return <Spin size="small" />;
          const enc = bomTraceByLineId[record.id];
          const { heat, dimension } = enc ? decodeHeatDimBucket(enc) : { heat: '', dimension: '' };
          const selectValue = heat && dimension ? encodeHeatDimBucket(heat, dimension) : undefined;
          return (
            <Select
              showSearch
              allowClear
              placeholder="Heat + dimension"
              style={{ width: '100%' }}
              value={selectValue}
              onChange={(v) => {
                setBomTraceByLineId((prev) => {
                  const next = { ...prev };
                  if (!v) delete next[record.id];
                  else next[record.id] = v;
                  return next;
                });
                setBomAvailabilityByLineId((prev) => {
                  const next = { ...prev };
                  delete next[record.id];
                  return next;
                });
              }}
              options={meta?.options ?? []}
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.trim().toLowerCase())
              }
            />
          );
        },
      },
      {
        title: 'Availability',
        key: 'availability',
        width: 170,
        render: (_: unknown, record: { id: string }) => {
          const avail = bomAvailabilityByLineId[record.id];
          if (!avail) return null;
          if (avail.requires_heat_dimension) {
            return <Tag color="default">Select heat + dimension</Tag>;
          }
          return avail.is_sufficient ? (
            <Tag icon={<CheckCircleOutlined />} color="success">
              Free: {avail.free_quantity ?? avail.available_quantity}
                {avail.on_hand_quantity != null ? ` (on hand ${avail.on_hand_quantity})` : ''}
            </Tag>
          ) : (
            <Tag icon={<WarningOutlined />} color="warning">
              Free: {avail.free_quantity ?? avail.available_quantity}
                {avail.on_hand_quantity != null ? ` / on hand ${avail.on_hand_quantity}` : ''}
            </Tag>
          );
        },
      },
    ],
    [
      bomAvailabilityByLineId,
      bomScaleFactor,
      itemsById,
      watchedWarehouseId,
      bomHeatMetaByLineId,
      bomTraceByLineId,
      bomDimsByLineId,
      showBoqLinePicker,
      boqLineSelectOptions,
      bomWoLineByBomLineId,
    ]
  );

  const boqEstTab = (
    <div>
      <Alert
        message="Material from Estimation / BOQ"
        description={
          isFabricationWo
            ? 'Fabrication: issue for one production unit or full line total. Quantities scale from BOQ materials via the customer order BOQ chain. Description-only BOQ materials need an inventory link here before create.'
            : 'Non-Fabrication: BOQ-line total quantity only (all operations on the line). Quantities scale from BOQ materials via the customer order BOQ chain. Description-only BOQ materials need an inventory link here before create.'
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      {isFabricationWo ? (
        <Form.Item label="Issue for" style={{ marginBottom: 16 }}>
          <Select
            value={boqIssueForUnit === 'full' ? 'full' : String(boqIssueForUnit)}
            onChange={(v) => {
              setBoqIssueForUnit(v === 'full' ? 'full' : Number(v));
              setBoqAvailabilityByLineKey({});
              setBoqTraceByLineKey({});
            }}
            options={[
              { value: 'full', label: 'Full line (all units)' },
              ...Array.from({ length: fabricationPlannedQty }, (_, i) => ({
                value: String(i + 1),
                label: `Production unit ${i + 1} only`,
              })),
            ]}
          />
        </Form.Item>
      ) : null}
      {boqPreview.isError ? (
        <Alert
          type="error"
          showIcon
          message="Could not load BOQ lines"
          description={
            boqPreviewErrorMessage ||
            'Check that this work order is from a customer order with a quotation BOQ.'
          }
          style={{ marginBottom: 16 }}
        />
      ) : null}
      <Form form={form} layout="vertical">
        <Form.Item name="source_warehouse_id" label="Source Warehouse">
          <Select
            showSearch
            placeholder="Select warehouse (must match opening stock / issue)"
            optionFilterProp="label"
            allowClear
            options={warehouses.map((w) => ({
              value: w.id,
              label: `${w.warehouse_code ?? w.id} — ${w.warehouse_name ?? ''}`,
            }))}
            onChange={() => {
              setBoqAvailabilityByLineKey({});
              setBoqTraceByLineKey({});
            }}
          />
        </Form.Item>
        <Form.Item
          name="department_id"
          label="Department"
          rules={[{ required: true, message: 'Department is required' }]}
        >
          <DepartmentSelect />
        </Form.Item>
        <Form.Item name="required_date" label="Required Date">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="priority" label="Priority" initialValue="Normal">
          <Select>
            <Select.Option value="Low">Low</Select.Option>
            <Select.Option value="Normal">Normal</Select.Option>
            <Select.Option value="High">High</Select.Option>
            <Select.Option value="Urgent">Urgent</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={3} placeholder="Enter any additional notes" />
        </Form.Item>
      </Form>

      <div style={{ marginTop: 16, marginBottom: 12 }}>
        <Button
          onClick={handleCheckBoqAvailability}
          loading={checkAvailabilityMutation.isPending}
          disabled={boqPreview.isPending || (boqPreview.data?.length ?? 0) === 0}
        >
          Check Availability
        </Button>
      </div>

      <div style={{ marginTop: 0 }}>
        <Table
          size="small"
          rowKey="line_key"
          loading={boqPreview.isPending}
          columns={boqPreviewColumns}
          dataSource={boqPreview.data ?? []}
          pagination={false}
          scroll={{ x: 'max-content' }}
          rowClassName={(record) => {
            const resolvedId = resolveBoqItemId(record);
            return getCriticalItemRowClassName({
              is_critical: resolvedId ? isItemCritical(itemsById.get(resolvedId)) : false,
            });
          }}
          locale={{
            emptyText: boqPreview.isPending
              ? 'Loading…'
              : 'No BOQ materials found. Add materials on the BOQ (linked or description-only).',
          }}
          rowSelection={{
            selectedRowKeys: boqSelectedKeys,
            onChange: (keys) => setBoqSelectedKeys(keys as string[]),
          }}
        />
      </div>
    </div>
  );

  const bomTab = (
    <div>
      <Alert
        message="Create from BOM"
        description="Choose a BOM linked to this work order or for the same finished item. Select which component lines to include. Quantities scale from the BOM base quantity to the work order planned quantity. Use Check availability after picking a source warehouse."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      {bomOptionsQuery.isError ? (
        <Alert
          type="error"
          showIcon
          message="Could not load BOM options"
          description={bomOptionsErrorMessage || 'Check the work order and BOM master data.'}
          style={{ marginBottom: 16 }}
        />
      ) : null}
      {!bomOptionsQuery.isPending && bomOptionsQuery.data?.boms?.length === 0 ? (
        <Alert
          type="warning"
          showIcon
          message="No BOMs available for this work order"
          description="Link a BOM on the work order or create a BOM for the work order finished item, then try again."
          style={{ marginBottom: 16 }}
        />
      ) : null}
      <Form form={form} layout="vertical">
        <Form.Item
          name="bom_id"
          label="Bill of materials"
          rules={[{ required: true, message: 'Select a BOM' }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            placeholder={bomOptionsQuery.isPending ? 'Loading BOMs…' : 'Select BOM'}
            loading={bomOptionsQuery.isPending}
            options={(bomOptionsQuery.data?.boms ?? []).map((b) => ({
              value: b.id,
              label: `${b.bom_code} — ${b.bom_name}${
                b.is_linked_on_work_order ? ' (on work order)' : b.is_default ? ' (default)' : ''
              }`,
            }))}
            onChange={() => {
              setBomAvailabilityByLineId({});
              setBomTraceByLineId({});
            }}
          />
        </Form.Item>
        <Form.Item name="source_warehouse_id" label="Source Warehouse">
          <Select
            showSearch
            placeholder="Select warehouse (must match opening stock / issue)"
            optionFilterProp="label"
            allowClear
            options={warehouses.map((w) => ({
              value: w.id,
              label: `${w.warehouse_code ?? w.id} — ${w.warehouse_name ?? ''}`,
            }))}
            onChange={() => {
              setBomAvailabilityByLineId({});
              setBomTraceByLineId({});
            }}
          />
        </Form.Item>
        <Form.Item
          name="department_id"
          label="Department"
          rules={[{ required: true, message: 'Department is required' }]}
        >
          <DepartmentSelect />
        </Form.Item>
        <div style={{ marginBottom: 12 }}>
          <Space wrap>
            <Button onClick={handleCheckBomAvailability} loading={checkAvailabilityMutation.isPending}>
              Check availability
            </Button>
            {bomOptionsQuery.data?.work_order?.planned_quantity != null && bomDetail?.quantity != null ? (
              <Text type="secondary">
                WO planned qty: {bomOptionsQuery.data.work_order.planned_quantity} · BOM base qty:{' '}
                {bomDetail.quantity} · Scale: ×{bomScaleFactor.toLocaleString(undefined, { maximumFractionDigits: 4 })}
              </Text>
            ) : null}
          </Space>
        </div>
        <Table
          rowKey="id"
          size="small"
          loading={bomDetailQuery.isPending}
          dataSource={bomDetail?.items ?? []}
          columns={bomColumns}
          pagination={false}
          scroll={{ x: 'max-content' }}
          rowClassName={(record) =>
            getCriticalItemRowClassName({ is_critical: isItemCritical(itemsById.get(record.item_id)) })
          }
          locale={{
            emptyText: selectedBomId
              ? 'This BOM has no component lines.'
              : 'Select a BOM to list components.',
          }}
          rowSelection={{
            selectedRowKeys: bomSelectedLineKeys,
            onChange: (keys) => setBomSelectedLineKeys(keys as string[]),
          }}
        />
        <Form.Item name="required_date" label="Required Date">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="priority" label="Priority" initialValue="Normal">
          <Select>
            <Select.Option value="Low">Low</Select.Option>
            <Select.Option value="Normal">Normal</Select.Option>
            <Select.Option value="High">High</Select.Option>
            <Select.Option value="Urgent">Urgent</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={3} placeholder="Enter any additional notes" />
        </Form.Item>
      </Form>
    </div>
  );

  const manualTab = (
    <div>
      <Form form={form} layout="vertical">
        <Form.Item name="source_warehouse_id" label="Source Warehouse">
          <Select
            showSearch
            placeholder="Select warehouse (must match opening stock / issue)"
            optionFilterProp="label"
            allowClear
            options={warehouses.map((w) => ({
              value: w.id,
              label: `${w.warehouse_code ?? w.id} — ${w.warehouse_name ?? ''}`,
            }))}
            onChange={() => {
              setManualAvailabilityByKey({});
              setManualItems((prev) =>
                prev.map((row) =>
                  needsMrHeatDimPicker(itemsById.get(row.item_id))
                    ? { ...row, heat_number: '', dimension: '' }
                    : row
                )
              );
            }}
          />
        </Form.Item>
        <Form.Item
          name="department_id"
          label="Department"
          rules={[{ required: true, message: 'Department is required' }]}
        >
          <DepartmentSelect />
        </Form.Item>
        <Form.Item name="required_date" label="Required Date">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="priority" label="Priority" initialValue="Normal">
          <Select>
            <Select.Option value="Low">Low</Select.Option>
            <Select.Option value="Normal">Normal</Select.Option>
            <Select.Option value="High">High</Select.Option>
            <Select.Option value="Urgent">Urgent</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} placeholder="Enter any additional notes" />
        </Form.Item>
      </Form>

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button type="dashed" onClick={handleAddItem} icon={<PlusOutlined />}>
            Add Item
          </Button>
          <Button
            onClick={handleCheckAvailability}
            loading={checkAvailabilityMutation.isPending}
          >
            Check Availability
          </Button>
        </Space>
      </div>

      <Table
        dataSource={manualItems}
        columns={manualItemsColumns}
        pagination={false}
        size="small"
        loading={loadingItems}
        scroll={{ x: 'max-content' }}
        rowClassName={(r) => getCriticalItemRowClassName(r)}
        locale={{ emptyText: 'No items added. Click "Add Item" to begin.' }}
      />
    </div>
  );

  const createLoading =
    createFromBOMMutation.isPending ||
    createFromBoqEstMutation.isPending ||
    createManualMutation.isPending ||
    updateMutation.isPending;
  const handleCreateClick = isEdit
    ? handleUpdateDraft
    : activeTab === 'bom'
      ? handleCreateFromBOM
      : activeTab === 'boq'
        ? handleCreateFromBoqEstimation
        : handleCreateManual;
  const createSubmitDisabled = !isEdit && (iirBlockLoading || iirBlocksMr);
  const primaryActionLabel = isEdit ? 'Save Changes' : 'Create Request';
  const iirBlockedAlert =
    !isEdit && (iirBlockLoading || iirBlocksMr) ? (
      <Alert
        style={{ marginBottom: 16 }}
        type="warning"
        showIcon
        message={
          iirBlockLoading ? 'Checking Job Inspection status…' : 'Job Inspection required'
        }
        description={
          iirBlockLoading
            ? undefined
            : 'Approve the Job Inspection for this work order before requesting materials. Open the Job Inspection tab on the work order, or use QMS → Initial Inspection Requests.'
        }
      />
    ) : null;
  const tabsNode = loadingExistingMr && isEdit ? (
    <div style={{ textAlign: 'center', padding: 40 }}>
      <Spin size="large" />
    </div>
  ) : (
    <Tabs
      activeKey={activeTab}
      onChange={(key) => setActiveTab(key as 'bom' | 'boq' | 'manual')}
      items={
        isEdit || isMaintenanceCase
          ? [
              {
                key: 'manual',
                label: isEdit ? 'Request Lines' : 'Manual Entry',
                children: (
                  <>
                    {isEdit ? (
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="Editing draft material request"
                        description="Update warehouse, priority, notes, and line items. Save keeps the request in Draft."
                      />
                    ) : (
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="Linked to maintenance case"
                        description="This request posts against Inventory with reference AssetMaintenanceCase — no manufacturing work order."
                      />
                    )}
                    {manualTab}
                  </>
                ),
              },
            ]
          : isProject
            ? [
                {
                  key: 'manual',
                  label: 'Manual Entry',
                  children: (
                    <>
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="Project material request"
                        description="Manual lines only — linked to the selected project (no work order or BOM)."
                      />
                      {manualTab}
                    </>
                  ),
                },
              ]
            : [
              {
                key: 'bom',
                label: 'From BOM',
                children: bomTab,
              },
              {
                key: 'boq',
                label: 'Material from Estimation / BOQ',
                children: boqEstTab,
              },
              {
                key: 'manual',
                label: 'Manual Entry',
                children: manualTab,
              },
            ]
      }
    />
  );

  if (mode === 'page') {
    return (
      <Card
        title={title}
        extra={
          <Space>
            {onBack && <Button onClick={onBack}>Back</Button>}
            <Button onClick={onClose}>Cancel</Button>
            <Tooltip
              title={
                isEdit
                  ? undefined
                  : iirBlockLoading
                    ? 'Checking Job Inspection status…'
                    : iirBlocksMr
                      ? 'Job Inspection must be approved before requesting materials.'
                      : undefined
              }
            >
              <span>
                <Button
                  key="submit"
                  type="primary"
                  loading={createLoading}
                  disabled={createSubmitDisabled || (isEdit && loadingExistingMr)}
                  onClick={handleCreateClick}
                >
                  {primaryActionLabel}
                </Button>
              </span>
            </Tooltip>
          </Space>
        }
      >
        {iirBlockedAlert}
        {tabsNode}
      </Card>
    );
  }

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onClose}
      width={960}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Tooltip
          key="submit-wrap"
          title={
            isEdit
              ? undefined
              : iirBlockLoading
                ? 'Checking Job Inspection status…'
                : iirBlocksMr
                  ? 'Job Inspection must be approved before requesting materials.'
                  : undefined
          }
        >
          <span>
            <Button
              key="submit"
              type="primary"
              loading={createLoading}
              disabled={createSubmitDisabled || (isEdit && loadingExistingMr)}
              onClick={handleCreateClick}
            >
              {primaryActionLabel}
            </Button>
          </span>
        </Tooltip>,
      ]}
    >
      {iirBlockedAlert}
      {tabsNode}
    </Modal>
  );
};
